// JARVIS Master Brain V2 — Weighted Consensus Engine
// Collects reports from ALL active strategy agents (dynamic count).
// Applies win-rate weighting → calculates consensus → generates final verdict.
// This is the final intelligence layer. All agents report here.

import { apiUrl } from '../services/apiBase';
import { runAllAgents } from './agentFactory';
import { getAgentEligibleStrategies } from './strategyLifecycle';
import { getFullJarvisContext } from '../services/storage';
import { getTrades } from '../services/storage';

// ── Weighted consensus calculation ────────────────────────────────────────────
// Each agent votes BUY/SELL/WAIT weighted by its agentWeight (based on win rate + status).
// Returns a detailed breakdown of the consensus.

export function calculateWeightedConsensus(reports) {
  const validReports = reports.filter(r => r.status === 'ok' && r.verdict);
  if (validReports.length === 0) {
    return { verdict: 'WAIT', buyWeight: 0, sellWeight: 0, waitWeight: 0, totalWeight: 0, confidence: 0, agentBreakdown: [] };
  }

  let buyWeight  = 0;
  let sellWeight = 0;
  let waitWeight = 0;
  let totalWeight = 0;

  const agentBreakdown = validReports.map(r => {
    const w = r.agentWeight || 1;
    totalWeight += w;
    if (r.verdict === 'BUY')  buyWeight  += w;
    if (r.verdict === 'SELL') sellWeight += w;
    if (r.verdict === 'WAIT') waitWeight += w;

    return {
      name:     r.strategyName,
      dna:      r.dna,
      verdict:  r.verdict,
      weight:   w,
      winRate:  r.winRate,
      confidence: r.confidence,
    };
  });

  // Determine winning verdict by highest weight
  let verdict = 'WAIT';
  let winningWeight = waitWeight;
  if (buyWeight > winningWeight)  { verdict = 'BUY';  winningWeight = buyWeight;  }
  if (sellWeight > winningWeight) { verdict = 'SELL'; winningWeight = sellWeight; }

  // Consensus strength: % of total weight that agrees with verdict
  const consensusStrength = totalWeight > 0 ? Math.round((winningWeight / totalWeight) * 100) : 0;

  // Agent agreement count
  const agreeing = validReports.filter(r => r.verdict === verdict).length;

  // Best entry/SL/TP from agreeing agents (weighted average)
  const agreeingReports = validReports.filter(r => r.verdict === verdict);
  const weightedAvg = (field) => {
    const valid = agreeingReports.filter(r => r[field]);
    if (!valid.length) return null;
    const sum = valid.reduce((s, r) => s + r[field] * r.agentWeight, 0);
    const wt  = valid.reduce((s, r) => s + r.agentWeight, 0);
    return Math.round((sum / wt) * 100) / 100;
  };

  // Risk check: if ANY agent says AVOID, flag it
  const riskWarning = reports.some(r => r.riskVerdict === 'AVOID');
  const riskCaution = reports.some(r => r.riskVerdict === 'CAUTION');

  return {
    verdict,
    consensusStrength,
    buyWeight:  Math.round(buyWeight  * 10) / 10,
    sellWeight: Math.round(sellWeight * 10) / 10,
    waitWeight: Math.round(waitWeight * 10) / 10,
    totalWeight: Math.round(totalWeight * 10) / 10,
    agentCount:    validReports.length,
    agreeingCount: agreeing,
    suggestedEntry: weightedAvg('target1') ? null : null, // use entryZone from text
    suggestedSL:    weightedAvg('stopLoss'),
    suggestedTP1:   weightedAvg('target1'),
    suggestedTP2:   weightedAvg('target2'),
    riskWarning,
    riskCaution,
    agentBreakdown: agentBreakdown.sort((a, b) => b.weight - a.weight),
    errorCount: reports.filter(r => r.status === 'error').length,
  };
}

// ── Master synthesizer — final Groq call ──────────────────────────────────────
// Takes the consensus + all agent reports → generates human-readable final verdict.

async function callMasterSynthesizer(question, consensus, allReports, context) {
  const { currentPrice, currentSession, riskSettings } = context;

  const topAgents = allReports
    .filter(r => r.status === 'ok')
    .sort((a, b) => b.agentWeight - a.agentWeight)
    .slice(0, 8); // Show top 8 in summary (all counted in consensus, top 8 in prompt)

  const agentSummary = topAgents.map(r =>
    `[${r.dna} Agent — ${r.winRate}% WR (real, from closed trades) — Weight ${r.agentWeight}x] → ${r.verdict}: ${r.reason}`
  ).join('\n');

  const systemPrompt = `You are JARVIS Master Brain — the final intelligence that synthesizes ALL strategy agents.
All active agents have analyzed the market. Your job: deliver the definitive verdict to Sir.
Be specific with prices. Address Sir directly. Return ONLY valid JSON.`;

  const userMessage = `Sir's question: "${question}"

CURRENT PRICE: ${currentPrice}
SESSION: ${currentSession?.name || 'Unknown'}
ACCOUNT: $${riskSettings?.account_balance || 1000} | Risk: ${riskSettings?.risk_per_trade_percent || 1}%

WEIGHTED CONSENSUS FROM ALL ${consensus.agentCount} ACTIVE AGENTS:
Verdict: ${consensus.verdict}
Consensus Strength: ${consensus.consensusStrength}% (${consensus.agreeingCount}/${consensus.agentCount} agents agree)
BUY Weight: ${consensus.buyWeight} | SELL Weight: ${consensus.sellWeight} | WAIT Weight: ${consensus.waitWeight}
Risk Warning: ${consensus.riskWarning ? 'YES — one or more agents flagged AVOID' : 'Clear'}
${consensus.riskCaution ? 'Caution flagged by at least one agent.' : ''}

TOP AGENT REPORTS (by weight):
${agentSummary}

Suggested SL: ${consensus.suggestedSL || 'See agents'}
Suggested TP1: ${consensus.suggestedTP1 || 'See agents'}
Suggested TP2: ${consensus.suggestedTP2 || 'See agents'}

Synthesize ALL of this and return final JSON. Do NOT invent a numeric
confidence — "consensus_strength" above is already the real, computed
% of agent weight agreeing with the verdict; use that, don't add a second
made-up number on top of it.
{
  "final_verdict": "BUY|SELL|WAIT",
  "grade": "A+|A|B|C|D",
  "entry_zone": "price range string or null",
  "stop_loss": price_or_null,
  "tp1": price_or_null,
  "tp2": price_or_null,
  "lot_size": number_or_null,
  "risk_reward": "1:X or null",
  "master_reasoning": "2-3 sentences — WHY this verdict. Which key agents drove it. What conditions are most important right now.",
  "key_risks": ["risk1", "risk2"],
  "immediate_action": "Exact instruction: what Sir should do RIGHT NOW.",
  "dissenting_agents": "Brief note if any high-weight agents disagreed — why to be careful",
  "market_summary": "1 sentence current market state"
}`;

  const response = await fetch(apiUrl('/api/chat'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      system: systemPrompt,
      messages: [{ role: 'user', content: userMessage }],
      max_tokens: 800,
    }),
  });

  if (!response.ok) throw new Error(`Master Brain HTTP ${response.status}`);
  const data = await response.json();
  const text = data.content?.[0]?.text || '';

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('Master Brain returned no JSON');
  return JSON.parse(jsonMatch[0]);
}

// ── Main entry point ──────────────────────────────────────────────────────────
// 1. Get context
// 2. Run ALL strategy agents (dynamic count)
// 3. Calculate weighted consensus
// 4. Run Master Synthesizer
// 5. Return complete analysis

export async function runMasterAnalysis(question = 'Should I trade now?', onStep) {
  const eligible = getAgentEligibleStrategies();

  // Step 0: Check if any agents exist
  if (eligible.length === 0) {
    return {
      success: false,
      error: 'No active strategies yet. Log trades and earn wins to build your agent network.',
      agentCount: 0,
    };
  }

  const context = getFullJarvisContext();
  const allReports = [];
  let step = 0;
  const total = eligible.length + 1; // +1 for Master Brain

  onStep?.({ step: 0, name: 'Launching Agents', status: 'running', total: eligible.length });

  // Run ALL agents with progress callback
  const reports = await runAllAgents(context, (report, idx, totalAgents) => {
    step = idx + 1;
    onStep?.({
      step,
      name:        report.strategyName,
      dna:         report.dna,
      status:      report.status === 'ok' ? 'done' : 'error',
      verdict:     report.verdict,
      confidence:  report.confidence,
      winRate:     report.winRate,
      weight:      report.agentWeight,
      report,
      total:       totalAgents,
    });
    allReports.push(report);
  });

  // Calculate weighted consensus from ALL agents
  const consensus = calculateWeightedConsensus(reports);

  onStep?.({
    step: eligible.length + 1,
    name: 'Master Brain',
    status: 'running',
    message: `Synthesizing ${consensus.agentCount} agent reports...`,
    total: eligible.length + 1,
  });

  // Run Master Synthesizer
  let masterVerdict;
  try {
    masterVerdict = await callMasterSynthesizer(question, consensus, reports, context);
  } catch (err) {
    // Fallback: use consensus directly if Master Brain Groq call fails
    masterVerdict = {
      final_verdict:      consensus.verdict,
      master_confidence:  consensus.consensusStrength,
      grade:              consensus.consensusStrength >= 80 ? 'A' : consensus.consensusStrength >= 60 ? 'B' : 'C',
      stop_loss:          consensus.suggestedSL,
      tp1:                consensus.suggestedTP1,
      tp2:                consensus.suggestedTP2,
      master_reasoning:   `${consensus.agreeingCount} of ${consensus.agentCount} agents voted ${consensus.verdict} with ${consensus.consensusStrength}% weighted consensus.`,
      immediate_action:   `Consensus is ${consensus.verdict} — review individual agent reports for details.`,
      key_risks:          consensus.riskWarning ? ['Risk warning from agent(s)'] : [],
    };
  }

  onStep?.({
    step: eligible.length + 1,
    name:   'Master Brain',
    status: 'done',
    verdict: masterVerdict.final_verdict,
    total:   eligible.length + 1,
  });

  return {
    success:         true,
    question,
    agentCount:      reports.length,
    agentReports:    reports,
    consensus,
    masterVerdict,
    runAt:           new Date().toISOString(),
    context: {
      price:   context.currentPrice,
      session: context.currentSession?.name,
    },
  };
}
